import Phaser from 'phaser';
import { GAME_CONFIG, PLAYER_CONFIG } from '../../config/game';
import { BaseBossScene, BossSceneConfig } from './BaseBossScene';

/**
 * MorningTitanScene (Level 1-B - World Boss)
 * 
 * Theme: Epic morning routine boss - the personification of morning chaos
 * Multi-phase boss combining elements from all World 1 levels
 * Uses Sobel edge physics from BaseBossScene
 */
export class MorningTitanScene extends BaseBossScene {
  private boss!: Phaser.GameObjects.Container;
  private bossHealth: number = 90; // Reduced from 120
  private maxBossHealth: number = 90;
  private phase: number = 1;
  private attackTimer?: Phaser.Time.TimerEvent;
  private isAttacking: boolean = false;
  private bossSprite!: Phaser.GameObjects.Graphics;
  private lastAttackTime: number = 0;
  private attackCooldown: number = 2500; // Slower attacks (was 2000)

  constructor() {
    const config: BossSceneConfig = {
      levelId: '1-B',
      levelName: 'Morning Routine Titan',
      backgroundKey: '1-B_morning_titan',
      bossName: 'THE MORNING CHAOS',
      useEdgePhysics: true, // Use BaseBossScene's edge physics
    };
    super('MorningTitanScene', config);
  }

  create() {
    // Load edge data before base create
    this.loadEdgeData();
    
    // Call parent create (sets up player, platforms, projectiles, UI, controls)
    super.create();
    
    const { width, height } = GAME_CONFIG;
    
    // Create gold edge overlay
    this.createEdgeOverlay(width, height);
    
    // Ensure player renders in front
    this.player.setDepth(100);
  }

  protected createBoss(width: number, height: number) {
    // Create the Morning Titan - a swirling vortex of morning items
    this.boss = this.add.container(width * 0.7, height - 250);
    this.boss.setDepth(50);

    // Boss body - swirling chaos cloud
    this.bossSprite = this.add.graphics();
    this.drawBossPhase1();
    this.boss.add(this.bossSprite);

    // Add physics body
    this.physics.add.existing(this.boss);
    const body = this.boss.body as Phaser.Physics.Arcade.Body;
    body.setSize(120, 120);
    body.setImmovable(true);
    body.setAllowGravity(false);

    // Boss collision with player
    this.physics.add.overlap(
      this.player,
      this.boss,
      this.handleBossCollision as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
      undefined,
      this
    );

    // Idle floating animation
    this.tweens.add({
      targets: this.boss,
      y: height - 270,
      duration: 1500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Start attack pattern after intro
    this.safeDelayedCall(2500, () => {
      this.startAttackPattern();
    });
  }

  private drawBossPhase1() {
    this.bossSprite.clear();
    
    // Phase 1: Blanket-themed - purple swirling mass
    this.bossSprite.fillStyle(0x9b59b6, 0.8);
    this.bossSprite.fillCircle(0, 0, 60);
    this.bossSprite.fillStyle(0x8e44ad, 0.6);
    this.bossSprite.fillCircle(-20, -10, 30);
    this.bossSprite.fillCircle(15, 20, 25);
    
    // Angry eyes
    this.bossSprite.fillStyle(0xff0000, 1);
    this.bossSprite.fillCircle(-20, -15, 8);
    this.bossSprite.fillCircle(20, -15, 8);
    this.bossSprite.fillStyle(0xffffff, 1);
    this.bossSprite.fillCircle(-18, -17, 3);
    this.bossSprite.fillCircle(22, -17, 3);
  }

  private drawBossPhase2() {
    this.bossSprite.clear();
    
    // Phase 2: Clothes-themed - more chaotic, colorful
    this.bossSprite.fillStyle(0xe74c3c, 0.8);
    this.bossSprite.fillCircle(0, 0, 65);
    this.bossSprite.fillStyle(0x3498db, 0.6);
    this.bossSprite.fillCircle(-25, -5, 35);
    this.bossSprite.fillStyle(0x2ecc71, 0.6);
    this.bossSprite.fillCircle(20, 15, 30);
    
    // Angrier eyes
    this.bossSprite.fillStyle(0xffff00, 1);
    this.bossSprite.fillCircle(-20, -15, 10);
    this.bossSprite.fillCircle(20, -15, 10);
    this.bossSprite.fillStyle(0x000000, 1);
    this.bossSprite.fillCircle(-18, -15, 4);
    this.bossSprite.fillCircle(22, -15, 4);
  }

  private drawBossPhase3() {
    this.bossSprite.clear();
    
    // Phase 3: Final form - intense, fiery
    this.bossSprite.fillStyle(0xe74c3c, 0.9);
    this.bossSprite.fillCircle(0, 0, 70);
    this.bossSprite.fillStyle(0xf39c12, 0.7);
    this.bossSprite.fillCircle(-30, -10, 40);
    this.bossSprite.fillStyle(0xf1c40f, 0.5);
    this.bossSprite.fillCircle(25, 20, 35);
    
    // Rage eyes
    this.bossSprite.fillStyle(0xff0000, 1);
    this.bossSprite.fillCircle(-25, -20, 12);
    this.bossSprite.fillCircle(25, -20, 12);
    this.bossSprite.fillStyle(0xffffff, 1);
    this.bossSprite.fillCircle(-23, -22, 5);
    this.bossSprite.fillCircle(27, -22, 5);
    
    // Angry mouth
    this.bossSprite.lineStyle(4, 0x000000);
    this.bossSprite.beginPath();
    this.bossSprite.moveTo(-20, 20);
    this.bossSprite.lineTo(0, 30);
    this.bossSprite.lineTo(20, 20);
    this.bossSprite.strokePath();
  }

  private startAttackPattern() {
    if (!this.isSceneActive()) return;
    
    this.attackTimer = this.time.addEvent({
      delay: this.attackCooldown,
      callback: () => {
        if (this.isSceneActive()) {
          this.performAttack();
        }
      },
      callbackScope: this,
      loop: true,
    });
  }

  private performAttack() {
    if (this.isGameOver || this.isPaused || this.isAttacking || !this.isSceneActive()) return;

    const now = Date.now();
    if (now - this.lastAttackTime < this.attackCooldown) return;
    this.lastAttackTime = now;

    this.isAttacking = true;

    // Choose attack based on phase
    const attackType = Phaser.Math.Between(0, 2);
    
    // Show warning first, then attack
    this.showAttackWarning(() => {
      if (this.phase === 1 || attackType === 0) {
        this.attackBlanketWave();
      } else if (this.phase === 2 || attackType === 1) {
        this.attackFlyingClothes();
      } else {
        this.attackShoeBarrage();
      }
    });

    this.safeDelayedCall(1500, () => {
      this.isAttacking = false;
    });
  }
  
  private showAttackWarning(callback: () => void) {
    if (!this.isSceneActive() || !this.boss) {
      callback();
      return;
    }
    
    // Flash boss with "!" indicator
    const warning = this.add.text(this.boss.x, this.boss.y - 80, '❗', {
      fontSize: '32px',
    }).setOrigin(0.5).setDepth(150);
    
    // Screen edge flash
    const flash = this.add.graphics();
    flash.fillStyle(0xff0000, 0.2);
    flash.fillRect(0, 0, GAME_CONFIG.width, GAME_CONFIG.height);
    flash.setDepth(50);
    
    // Pulse warning
    this.tweens.add({
      targets: [warning, flash],
      alpha: { from: 1, to: 0.3 },
      duration: 150,
      yoyo: true,
      repeat: 2,
      onComplete: () => {
        if (warning && warning.scene) warning.destroy();
        if (flash && flash.scene) flash.destroy();
        callback();
      },
    });
  }

  private attackBlanketWave() {
    if (!this.isSceneActive()) return;
    
    // Spawn multiple blanket projectiles in a wave
    for (let i = 0; i < 3; i++) {
      this.safeDelayedCall(i * 200, () => {
        if (this.isGameOver || !this.boss) return;
        
        const proj = this.add.graphics();
        proj.fillStyle(0xe879f9, 1);
        proj.fillRoundedRect(-20, -15, 40, 30, 8);
        
        const container = this.add.container(this.boss.x, this.boss.y, [proj]);
        this.physics.add.existing(container);
        
        const body = container.body as Phaser.Physics.Arcade.Body;
        body.setSize(40, 30);
        body.setAllowGravity(false);
        
        // Aim at player - slower projectiles for easier dodging
        const angle = Phaser.Math.Angle.Between(
          this.boss.x, this.boss.y,
          this.player.x, this.player.y
        );
        const speed = 160; // Slower (was 200)
        body.setVelocity(
          Math.cos(angle) * speed,
          Math.sin(angle) * speed
        );
        
        this.projectiles.add(container);
        
        this.safeDelayedCall(3000, () => {
          if (container && container.scene) container.destroy();
        });
      });
    }
  }

  private attackFlyingClothes() {
    if (!this.isSceneActive()) return;
    
    const { width, height } = GAME_CONFIG;
    
    // Spawn clothes from sides - slower for easier dodging
    const side = Phaser.Math.Between(0, 1);
    const startX = side === 0 ? -30 : width + 30;
    const velocityX = side === 0 ? 180 : -180; // Slower (was 250)
    
    for (let i = 0; i < 4; i++) {
      this.safeDelayedCall(i * 150, () => {
        if (this.isGameOver) return;
        
        const y = Phaser.Math.Between(height - 350, height - 150);
        
        const proj = this.add.graphics();
        const color = [0xff6b6b, 0x4ecdc4, 0xffd93d][Phaser.Math.Between(0, 2)];
        proj.fillStyle(color, 1);
        proj.fillRect(-15, -10, 30, 25);
        proj.fillRect(-22, -10, 7, 12);
        proj.fillRect(15, -10, 7, 12);
        
        const container = this.add.container(startX, y, [proj]);
        this.physics.add.existing(container);
        
        const body = container.body as Phaser.Physics.Arcade.Body;
        body.setSize(35, 25);
        body.setAllowGravity(false);
        body.setVelocityX(velocityX);
        
        // Spin
        this.tweens.add({
          targets: proj,
          rotation: Math.PI * 4,
          duration: 2000,
        });
        
        this.projectiles.add(container);
        
        this.safeDelayedCall(4000, () => {
          if (container && container.scene) container.destroy();
        });
      });
    }
  }

  private attackShoeBarrage() {
    if (!this.isSceneActive()) return;
    
    // Rain of shoes from above - fewer and slower
    for (let i = 0; i < 4; i++) { // 4 instead of 5
      this.safeDelayedCall(i * 400, () => { // Slower spawning (was 300)
        if (this.isGameOver) return;
        
        const { width } = GAME_CONFIG;
        const x = Phaser.Math.Between(50, width - 50);
        
        const proj = this.add.graphics();
        proj.fillStyle(0x8b4513, 1);
        proj.fillRoundedRect(-12, -6, 24, 12, 4);
        
        const container = this.add.container(x, -30, [proj]);
        this.physics.add.existing(container);
        
        const body = container.body as Phaser.Physics.Arcade.Body;
        body.setSize(24, 12);
        body.setAllowGravity(true);
        body.setGravityY(220); // Slower falling (was 300)
        
        this.projectiles.add(container);
        
        this.safeDelayedCall(3000, () => {
          if (container && container.scene) container.destroy();
        });
      });
    }
  }

  private handleBossCollision(
    player: Phaser.GameObjects.GameObject,
    _boss: Phaser.GameObjects.GameObject
  ) {
    if (this.isGameOver || !this.isSceneActive()) return;

    const playerObj = player as any;
    if (playerObj.isInvincible && playerObj.isInvincible()) return;

    const alive = playerObj.takeDamage();
    this.updateHearts();

    if (!alive) {
      this.onDefeat();
    }
  }

  protected handlePlayerAttack() {
    if (this.isGameOver || this.isPaused || !this.isSceneActive()) return;

    // Check if player is near boss
    const distance = Phaser.Math.Distance.Between(
      this.player.x, this.player.y,
      this.boss.x, this.boss.y
    );

    if (distance < 100) {
      this.damageBoss(10);
      
      // Knockback effect
      this.tweens.add({
        targets: this.boss,
        x: this.boss.x + 20,
        duration: 100,
        yoyo: true,
      });

      // Flash effect
      if (this.bossSprite && this.bossSprite.scene) {
        this.bossSprite.setAlpha(0.5);
        this.safeDelayedCall(100, () => {
          if (this.bossSprite && this.bossSprite.scene) {
            this.bossSprite.setAlpha(1);
          }
        });
      }
    }
  }

  protected onParry() {
    if (!this.isSceneActive()) return;
    
    // Parrying reflects damage back
    this.damageBoss(15);
    
    // Visual feedback
    const { width, height } = GAME_CONFIG;
    const text = this.add.text(width / 2, height / 2, 'PARRY!', {
      fontFamily: 'Georgia, serif',
      fontSize: '24px',
      color: '#ffd700',
    }).setOrigin(0.5).setDepth(150);

    this.tweens.add({
      targets: text,
      y: height / 2 - 50,
      alpha: 0,
      duration: 800,
      onComplete: () => {
        if (text && text.scene) text.destroy();
      },
    });
  }

  private damageBoss(amount: number) {
    if (!this.isSceneActive()) return;
    
    this.bossHealth -= amount;
    this.updateBossHealthBar(this.bossHealth / this.maxBossHealth);
    
    // Show damage number
    this.showDamageNumber(this.boss.x, this.boss.y - 50, amount);

    // Check phase transitions
    const healthPercent = this.bossHealth / this.maxBossHealth;
    
    if (healthPercent <= 0) {
      this.onVictory();
    } else if (healthPercent <= 0.33 && this.phase < 3) {
      this.phase = 3;
      this.attackCooldown = 1800; // More generous (was 1200)
      this.drawBossPhase3();
      this.showPhaseTransition('FINAL FORM!');
    } else if (healthPercent <= 0.66 && this.phase < 2) {
      this.phase = 2;
      this.attackCooldown = 2200; // More generous (was 1500)
      this.drawBossPhase2();
      this.showPhaseTransition('Phase 2!');
    }
  }

  private showDamageNumber(x: number, y: number, amount: number) {
    if (!this.isSceneActive()) return;
    
    const dmgText = this.add.text(x, y, `-${amount}`, {
      fontFamily: 'Georgia, serif',
      fontSize: '20px',
      color: '#ff4444',
      stroke: '#000000',
      strokeThickness: 3,
    }).setOrigin(0.5).setDepth(200);
    
    this.tweens.add({
      targets: dmgText,
      y: y - 40,
      alpha: 0,
      scale: 1.5,
      duration: 600,
      ease: 'Back.easeOut',
      onComplete: () => {
        if (dmgText && dmgText.scene) dmgText.destroy();
      },
    });
  }

  private showPhaseTransition(text: string) {
    if (!this.isSceneActive()) return;
    
    const { width, height } = GAME_CONFIG;
    
    const phaseText = this.add.text(width / 2, height / 2, text, {
      fontFamily: 'Georgia, serif',
      fontSize: '28px',
      color: '#ff4444',
      stroke: '#000000',
      strokeThickness: 4,
    }).setOrigin(0.5).setDepth(200);

    // Screen shake
    this.cameras.main.shake(500, 0.02);

    this.tweens.add({
      targets: phaseText,
      scale: { from: 0.5, to: 1.5 },
      alpha: { from: 1, to: 0 },
      duration: 1500,
      onComplete: () => {
        if (phaseText && phaseText.scene) phaseText.destroy();
      },
    });
  }

  protected updateBoss(_time: number, _delta: number) {
    if (this.isGameOver || !this.boss) return;

    // Boss movement - track player somewhat
    const targetX = Phaser.Math.Clamp(
      this.player.x + 100,
      GAME_CONFIG.width * 0.4,
      GAME_CONFIG.width * 0.85
    );
    
    this.boss.x += (targetX - this.boss.x) * 0.02;

    // Rotate boss sprite slightly
    if (this.bossSprite && this.bossSprite.scene) {
      this.bossSprite.rotation += 0.01;
    }
  }

  // Override update to handle edge physics boost for jump
  update(time: number, delta: number) {
    if (this.isPaused || this.isGameOver) return;

    // Handle player movement
    let moveX = 0;
    
    if (this.mobileControls) {
      const joystick = this.mobileControls.getMovementVector();
      if (joystick.x !== 0) {
        moveX = joystick.x;
      }
    }
    
    if (moveX === 0 && this.cursors) {
      if (this.cursors.left.isDown) {
        moveX = -1;
      } else if (this.cursors.right.isDown) {
        moveX = 1;
      }
    }

    this.player.setVelocityX(moveX * PLAYER_CONFIG.speed);

    // Jump with edge boost
    if (this.jumpKey?.isDown || this.cursors?.up.isDown || this.cursors?.space?.isDown) {
      if (this.player.isOnGround()) {
        this.player.jumpWithBoost(this.currentJumpBoost);
        this.currentJumpBoost = 1.0;
      }
    }

    // Dash
    if (this.dashKey?.isDown) {
      this.player.dash(moveX || 1);
    }

    // Attack
    if (this.attackKey?.isDown) {
      this.handlePlayerAttack();
    }

    // Update mobile controls
    this.mobileControls?.update();

    // Apply edge physics from BaseBossScene
    this.applyEdgePhysics();

    // Update boss
    this.updateBoss(time, delta);
  }

  // Clean up on destroy
  shutdown() {
    super.shutdown();
    this.attackTimer?.destroy();
    this.attackTimer = undefined;
  }
}
